import {
  WrapperUIRoot,
  WrapperBody,
  PlayerArea,
  PlayerContainer,
  StreamSurface,
  MenuContainer,
} from "./WrapperUI.styles";

import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { optionsList } from "../../app/actions-list";

import { CustomUI } from "../CustomUI";

import {
  // ArcwarePixelStreaming,
  // ArcwareApplication,
  ArcwareInit,
} from "@arcware-cloud/pixelstreaming-websdk";
import { DialogGeneric } from "../DialogGeneric";
import { StartScreen } from "../StartScreen";
import { CamerasWrapper } from "../CamerasWrapper";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";
import { PopUpSummary } from "../PopUpSummary";
import { Loader } from "../Loader";
import { FullScreenButton } from "../FullScreenButton";
import { DynamicViewportOverlay } from "../DynamicViewportOverlay";
import { useDevice } from "../../hooks/useDevice";
import { useResponsiveMenuLayout } from "../../hooks/useResponsiveMenuLayout";
import { Header } from "../Header";

const CONTAINER_ID = "shared-stream-container";

export const WrapperUI = () => {
  const {
    summaryOpen,
    closeSummary,
    showDynamicViewportOverlay,
    currentResolution,
    currentMenuOption,
  } = useSelectedOptions();
  const ref = useRef(null);
  const { isIOS } = useDevice();

  // NEW HOOK handles all layout logic
  const { isMobile, containerRef, baseSize, menuSize, animReady } =
    useResponsiveMenuLayout();

  const [videoInitialized, setVideoInitialized] = useState(false);
  const [applicationResponse, setApplicationResponse] = useState();
  const [arcwareApplication, setArcwareApplication] = useState();
  const [showStartScreen, setShowStartScreen] = useState(true);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuToggle = React.useCallback((open) => {
    setIsMenuOpen(open);
  }, []);

  const [showExportLoader, setShowExportLoader] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  const [showBgLoader, setShowBgLoader] = useState(false);

  const exportIntervalRef = React.useRef(null);
  const finishTimeoutRef = React.useRef(null);
  const downloadTimeoutRef = React.useRef(null);

  // Clear everything safely
  function resetExportTimers() {
    if (exportIntervalRef.current) clearInterval(exportIntervalRef.current);
    if (finishTimeoutRef.current) clearTimeout(finishTimeoutRef.current);
    if (downloadTimeoutRef.current) clearTimeout(downloadTimeoutRef.current);
    exportIntervalRef.current =
      finishTimeoutRef.current =
      downloadTimeoutRef.current =
        null;
  }
  // ---- PROGRESS API (no useCallback)
  function startExportProgress() {
    resetExportTimers();

    setShowExportLoader(true);
    setExportProgress(0);

    exportIntervalRef.current = setInterval(() => {
      setExportProgress((prev) => (prev >= 100 ? 100 : prev + 10));
    }, 1500);
  }

  function stopExportInterval() {
    if (exportIntervalRef.current) {
      clearInterval(exportIntervalRef.current);
      exportIntervalRef.current = null;
    }
  }

  function createFile(file) {
    try {
      // finish progress
      if (exportIntervalRef.current) clearInterval(exportIntervalRef.current);
      exportIntervalRef.current = null;
      setExportProgress(100);

      // close loader after 600ms
      finishTimeoutRef.current = setTimeout(() => {
        setShowExportLoader(false);

        // trigger download shortly after
        downloadTimeoutRef.current = setTimeout(() => {
          const blob = new Blob(file.data, { type: file.mimetype });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");

          const now = new Date();
          const timestamp =
            String(now.getDate()).padStart(2, "0") +
            String(now.getMonth() + 1).padStart(2, "0") +
            now.getFullYear() +
            "-" +
            String(now.getHours()).padStart(2, "0") +
            String(now.getMinutes()).padStart(2, "0") +
            String(now.getSeconds()).padStart(2, "0");

          a.href = url;
          a.download = `interbion_configuration_export-${currentResolution}-${timestamp}${file.extension}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 250);
      }, 600);
    } finally {
      // schedule a final cleanup just in case
      setTimeout(resetExportTimers, 2000);
    }
  }

  const handleDialogShow = (value, reason) => {
    if (reason === "backdropClick") return;
    setShowStartScreen(value);
  };

  useEffect(() => {
    if (ref.current) return;

    const { PixelStreaming, Application } = ArcwareInit(
      {
        // shareId: "share-83688715-1b16-4198-a44f-a9b8153e1690",
        shareId: "share-aa24d144-b77b-4450-ae2f-ec2a5ada6384",
      },
      {
        initialSettings: {
          AutoConnect: true,
          TouchInput: true,
          customUI: true,
        },
        settings: {
          audioButton: false,
          fullscreenButton: false,
          micButton: false,
          settingsButton: false,
          infoButton: false,
          connectionStrengthIcon: false,
          stopButton: false, //remove later
        },
      },
    );

    if (window.location.hostname === "localhost") {
      window.arc = {
        app: Application,
        aps: PixelStreaming,
      };
    }

    let timerId = null; // Use this if you're outside a React component/hook

    setArcwareApplication(Application);
    Application.getApplicationResponse((response) => {
      setApplicationResponse(response);
      window.file = Application.webRtcController.file;
      if (response.startsWith("CreateScreenshot")) createFile(window.file);

      if (response === `{ "Loading": "Start" }`) setShowBgLoader(true);
      if (response === `{ "Loading": "Finish" }`) {
        // 2. Clear any existing timer before setting a new one (safety)
        if (timerId) {
          clearTimeout(timerId);
        }

        // 3. Set the delayed call and store the ID
        timerId = setTimeout(() => {
          setShowBgLoader(false);
          timerId = null; // Clear the ID after execution
        }, 1000); // 1 second delay
      }

      return () => {
        stopExportInterval();
      };
    });

    PixelStreaming.videoInitializedHandler.add(() => setVideoInitialized(true));

    window.application = Application; // Dev-only
    ref.current = document.querySelector(`#${CONTAINER_ID}`);
    if (!ref.current) throw new Error("Ref.current is undefined.");
    ref.current.appendChild(Application.rootElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Measure MenuContainer width at mount and on resize */
  const menuRef = useRef(null);
  // const containerRef = useRef(null);
  const playerAreaRef = useRef(null);
  const streamRef = useRef(null);
  // state
  // const [baseWidth, setBaseWidth] = useState(0); // width of WrapperBody
  // const [menuWidth, setMenuWidth] = useState(0); // 30% of baseWidth
  const [shouldSendRes, setShouldSendRes] = useState(false);

  const handleSendResOnStart = (value) => {
    setShouldSendRes(value);
  };

  // measure the OUTER container (WrapperBody), never PlayerArea

  const sendResolutionCommand = React.useCallback(
    (width, height) => {
      if (!arcwareApplication?.emitUIInteraction) return;
      const descriptor = {
        Console: `r.setres ${Math.round(width)}x${Math.round(height)}w`,
      };
      arcwareApplication.emitUIInteraction(descriptor);
    },
    [arcwareApplication],
  );

  useLayoutEffect(() => {
    if (!streamRef.current || !arcwareApplication?.emitUIInteraction) return;

    let prevW = 0;
    let prevH = 0;
    let debounceTimer;

    const measureAndEmit = () => {
      const rect = streamRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { width, height } = rect;

      const w = Math.round(width);
      const h = Math.round(height);

      if (w !== prevW || h !== prevH) {
        prevW = w;
        prevH = h;
        sendResolutionCommand(w, h);
      }
    };

    // Emit once immediately when ready
    if (videoInitialized) measureAndEmit();

    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(measureAndEmit, 150);
    });

    observer.observe(streamRef.current);

    return () => {
      clearTimeout(debounceTimer);
      observer.disconnect();
    };
  }, [
    arcwareApplication,
    videoInitialized,
    sendResolutionCommand,
    shouldSendRes,
  ]);

  useEffect(() => {
    return () => {
      resetExportTimers();
      if (videoInitialized && arcwareApplication) {
        arcwareApplication.stream.removePlayer();
      }
    };
  }, [arcwareApplication, videoInitialized]);

  useEffect(() => {
    if (videoInitialized);
  }, [videoInitialized]);

  useEffect(() => {}, [applicationResponse]);

  const dynamicActionList = optionsList?.[currentMenuOption];

  const effectiveMenuOpen = animReady ? isMenuOpen : false;
  const playerAreaStyle = isMobile
    ? {
        width: "100%",
        height: "100%",
      }
    : {
        width: !animReady
          ? "100%" // first paint: no horizontal shift
          : effectiveMenuOpen
            ? baseSize // menu visible
            : baseSize + menuSize, // menu pushed out of viewport
      };

  const menuContainerStyle = !animReady
    ? isMobile
      ? { height: 0 }
      : { width: 0 }
    : isMobile
      ? { height: effectiveMenuOpen ? `${menuSize}px` : 0 }
      : { width: `${menuSize}px` };

  return (
    <>
      <WrapperUIRoot>
        <Header
          emitUIInteraction={
            videoInitialized &&
            typeof arcwareApplication?.emitUIInteraction === "function"
              ? arcwareApplication.emitUIInteraction
              : null
          }
          actionsList={optionsList?.options ?? []}
        />
        <WrapperBody ref={containerRef}>
          <PlayerArea
            isMobile={isMobile}
            ref={playerAreaRef}
            menuOpen={isMenuOpen}
            animReady={animReady}
            sx={{ ...playerAreaStyle }}
          >
            <PlayerContainer>
              {videoInitialized && !isIOS && <FullScreenButton />}
              <DynamicViewportOverlay open={showDynamicViewportOverlay} />
              <StartScreen
                onHandleClose={handleDialogShow}
                videoInitialized={videoInitialized}
                arcwareApplication={arcwareApplication}
                environmentLoading={showBgLoader}
                show={showStartScreen}
                onSendResOnStart={handleSendResOnStart}
                onHandleMenuToggle={handleMenuToggle}
                menuOpen={isMenuOpen}
              />
              <CamerasWrapper
                emitUIInteraction={arcwareApplication?.emitUIInteraction}
              />
              <StreamSurface
                id={CONTAINER_ID}
                ready={videoInitialized && !showStartScreen}
                ref={streamRef}
              />
            </PlayerContainer>
            <MenuContainer
              isMobile={isMobile}
              menuOpen={isMenuOpen}
              ref={menuRef}
              sx={{ ...menuContainerStyle }}
              animReady={animReady}
            >
              <CustomUI
                emitUIInteraction={arcwareApplication?.emitUIInteraction}
                applicationResponse={applicationResponse}
                actionsList={dynamicActionList}
                loading={false}
                showingOverlay={showStartScreen}
                onHandleStartExport={startExportProgress}
                videoInitialized={videoInitialized}
                onHandleMenuToggle={handleMenuToggle}
                menuOpen={isMenuOpen}
                isMobile={isMobile}
              />
            </MenuContainer>
          </PlayerArea>
        </WrapperBody>
      </WrapperUIRoot>
      <DialogGeneric
        show={summaryOpen}
        onHandleClose={(value, reason) => {
          if (reason === "backdropClick") return;
          if (!value) closeSummary();
        }}
        maxWidth="md"
        sx={{
          zIndex: 9999999,
          ".MuiDialog-paper": { overflow: "hidden" },
        }}
      >
        <PopUpSummary />
      </DialogGeneric>
      <Loader
        open={showExportLoader}
        value={exportProgress}
        progress={true}
        blurValue={2}
        bgOpacity={0.6}
        label="Exporting your image"
      />

      <Loader
        open={showBgLoader}
        progress={false}
        blurValue={40}
        bgOpacity={0.75}
        label="Loading environment"
      />
    </>
  );
};
